//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import React, { useRef, useState } from 'react';
import { List } from 'react-virtualized';
import styled from 'styled-components';

import {
  ILabelIndexObject,
  ILabelsSortField,
  LabelsSortField,
} from '@apis/gatewayMediaApiModel';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { MetatagText } from '@components/atoms/text/Text';
import VisuallyHiddenText from '@components/atoms/visually-hidden-text/VisuallyHiddenText';
import { ErrorMessage } from '@components/media-filters/ErrorMessage';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { Table } from '@components/organisms/table/Table';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { LabelRow } from '@content-drawer/filters/labels/LabelRow';
import {
  ManageLabelsTableHeaderCell,
  ManageLabelsTableHeaderCellProps,
} from '@content-drawer/filters/labels/ManageLabelsHeaderCell';
import {
  LabelDeleteConfirmationDialog,
  LabelDeleteRequest,
} from '@content-drawer/filters/labels/confirmations/LabelDeleteConfirmationDialog';
import {
  LabelUpdateConfirmationDialog,
  LabelUpdateRequest,
} from '@content-drawer/filters/labels/confirmations/LabelUpdateConfirmationDialog';
import { useAllLabelsQuery } from '@content-drawer/filters/labels/hooks/useAllLabelsQuery';
import { sortLabels } from '@content-drawer/filters/labels/utils/sortLabels';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';
import { getFuzzySearch } from '@utils/getFuzzySearch';
import { pluralize } from '@utils/stringUtils';

const TABLE_HEIGHT = 336;
const TABLE_WIDTH = 583;
const ROW_HEIGHT = 67;

export const ManageLabelsTable = () => {
  const [updateRequest, setUpdateRequest] = useState<LabelUpdateRequest>();
  const [deleteRequest, setDeleteRequest] = useState<LabelDeleteRequest>();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const [sortField, setSortField] = useState<ILabelsSortField>({
    field: LabelsSortField.LastUsed,
    isAscending: false,
  });
  const [filteredLabels, setFilteredLabels] = useState<ILabelIndexObject[]>([]);

  const { data: response, error, isFetching } = useAllLabelsQuery();
  const count = response?.data.count;
  const allLabels = response?.data.indexObjects ?? [];

  // If we have a searchQuery use the filteredLabels otherwise show allLabels
  const labels = sortLabels(
    isFilterApplied ? filteredLabels : allLabels,
    sortField,
  );

  const onSearchChanged = (searchTerm = '') => {
    setSearchQuery(searchTerm);
    if (searchTerm.length === 0) {
      setIsFilterApplied(false);
    }
  };

  const handleFetch = async (searchTerm = '') => {
    setIsFilterApplied(searchTerm.length > 0);
    setFilteredLabels(
      getFuzzySearch({
        itemsToSearch: allLabels, // We are searching labels on the frontend side and do not need to track the search term.
        searchKeys: ['userTag'],
        searchTerm,
      }),
    );
  };

  const onDeleteLabel = (request: LabelDeleteRequest) => {
    setDeleteRequest(request);
  };

  const onUpdateLabel = (request: LabelUpdateRequest) => {
    setUpdateRequest(request);
  };

  const onCanceled = () => {
    setUpdateRequest(undefined);
    setDeleteRequest(undefined);
  };

  const onCompleted = () => {
    setUpdateRequest(undefined);
    setDeleteRequest(undefined);
  };

  const tableHeaderCells: ManageLabelsTableHeaderCellProps[] = [
    {
      isActive: LabelsSortField.UserTag === sortField.field,
      isAscending:
        LabelsSortField.UserTag === sortField.field && sortField.isAscending,
      justifyContent: 'flex-start',
      label: 'LABEL',
      minWidth: `calc(5 * ${theme.spacings[5]})`,
      onChange: (isAscending) =>
        setSortField({ field: LabelsSortField.UserTag, isAscending }),
    },
    {
      isActive: LabelsSortField.ItemsWithTag === sortField.field,
      isAscending:
        LabelsSortField.ItemsWithTag === sortField.field &&
        sortField.isAscending,
      justifyContent: 'flex-end',
      label: 'MEDIA ITEMS',
      minWidth: `calc(${theme.spacings[7]} + ${theme.spacings[7]} + ${theme.spacings[2]})`,
      onChange: (isAscending) =>
        setSortField({ field: LabelsSortField.ItemsWithTag, isAscending }),
    },
    {
      isActive: LabelsSortField.LastUsed === sortField.field,
      isAscending:
        LabelsSortField.LastUsed === sortField.field && sortField.isAscending,
      justifyContent: 'flex-start',
      label: 'LAST USED',
      minWidth: `calc(2 * ${theme.spacings[7]})`,
      onChange: (isAscending) =>
        setSortField({ field: LabelsSortField.LastUsed, isAscending }),
    },
  ];

  return (
    <>
      {!isDefined(updateRequest) && !isDefined(deleteRequest) && (
        <>
          <SearchBarContainer>
            {isDefined(count) && count > 0 && (
              <>
                <StyledSearchBar
                  ref={searchRef}
                  handleClear={() => onSearchChanged('')}
                  handleFetch={handleFetch}
                  onChange={onSearchChanged}
                  placeholder='Search'
                  trackingIdPrefix={ContentDrawerTrackingIds.SEARCH_LABELS}
                  value={searchQuery}
                  visuallyHiddenLabel='Search Labels'
                />
                <LabelsCount>{pluralize(count, 'label')}</LabelsCount>
              </>
            )}
            {isFetching && <CenteredSpinner />}
          </SearchBarContainer>
          <LabelsTableWrapper>
            {!isFetching && (
              <StyledTable
                body={
                  <>
                    <div>
                      {error && <Error error={error.message} />}
                      {allLabels.length >= 0 &&
                        isFilterApplied &&
                        filteredLabels.length === 0 && (
                          <NoLabelMessage>
                            No labels found. Adjust your search and try again.
                          </NoLabelMessage>
                        )}
                      {!error && allLabels.length === 0 && (
                        <NoLabelMessage>No labels found.</NoLabelMessage>
                      )}
                    </div>
                    <List
                      height={TABLE_HEIGHT}
                      rowCount={labels.length}
                      rowHeight={ROW_HEIGHT}
                      rowRenderer={({ index, key, style }) => (
                        <div key={key} style={style}>
                          {labels[index] && (
                            <LabelRow
                              label={labels[index]}
                              onDeleteLabel={onDeleteLabel}
                              onUpdateLabel={onUpdateLabel}
                            />
                          )}
                        </div>
                      )}
                      width={TABLE_WIDTH}
                    />
                  </>
                }
                caption={<VisuallyHiddenText>Labels</VisuallyHiddenText>}
                headerCells={[
                  ...tableHeaderCells.map((cell) => (
                    <ManageLabelsTableHeaderCell {...cell} key={cell.label} />
                  )),
                  '',
                ]}
              />
            )}
          </LabelsTableWrapper>
        </>
      )}
      {updateRequest && (
        <LabelUpdateConfirmationDialog
          isOpen={isDefined(updateRequest)}
          onCanceled={onCanceled}
          onCompleted={onCompleted}
          onUpdateLabel={onUpdateLabel}
          request={updateRequest}
        />
      )}
      {deleteRequest && (
        <LabelDeleteConfirmationDialog
          isOpen={isDefined(deleteRequest)}
          onCanceled={onCanceled}
          onCompleted={onCompleted}
          request={deleteRequest}
        />
      )}
    </>
  );
};

const StyledSearchBar = styled(SearchBar)`
  && {
    display: flex;
    width: 250px;
  }
`;

// Customize the table component with a scrollable and fixed height for the `tbody` and a fixed, always visible `thead`.
const StyledTable = styled(Table)(
  ({ theme }) => `

    tbody, thead tr {
      display: block;
    }

    tbody {
      display: block;
    }

    tbody .ReactVirtualized__Grid__innerScrollContainer {
      border-bottom: 1px solid ${theme.colors.gray1};
    }

    tbody td, thead th {
      width: 30%;
    }

    tbody td:last-child, thead th:last-child {
      width: 10%;
    }
`,
);

const LabelsCount = styled.div`
  ${Mixins.Text.UiCopySecondary};
  margin-left: ${({ theme }) => theme.spacings[2]};
`;

const NoLabelMessage = styled(MetatagText)(
  ({ theme }) => `
    width: 100%;
    padding: ${theme.spacings[5]};
    text-align: center;
`,
);

const Error = styled(ErrorMessage)(
  ({ theme }) => `
    padding: ${theme.spacings[5]};
  `,
);

const LabelsTableWrapper = styled.div(
  ({ theme }) => `
    width: 100%;
    height: ${theme.mediumMeasure};
    margin-top: ${theme.spacings[2]};
  `,
);

const SearchBarContainer = styled.div`
  display: flex;
  align-items: center;
`;
